import React, { useState, useEffect } from 'react';

const DEFAULT_AVATAR = 'data:image/svg+xml,' + encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" width="100" height="120" viewBox="0 0 100 120">
    <rect width="100" height="120" rx="8" fill="#f1f5f9" stroke="#cbd5e1" stroke-width="2"/>
    <circle cx="50" cy="42" r="22" fill="#94a3b8"/>
    <ellipse cx="50" cy="100" rx="34" ry="24" fill="#94a3b8"/>
  </svg>
`);

const StudentInformation = ({ studentConfig = {}, student = {}, academicYear = '', term = '', studentPhoto = '', photoPos = 'top-right' }) => {
  const cfg = studentConfig || {};
  const isKindergarten = student.className === 'UKG' || student.className === 'LKG';

  const [imgSrc, setImgSrc] = useState(studentPhoto || DEFAULT_AVATAR);

  useEffect(() => {
    setImgSrc(studentPhoto || DEFAULT_AVATAR);
  }, [studentPhoto]);

  const layout = cfg.layout || 'three_column';
  const gridCols = layout === 'two_column' ? 'repeat(2, 1fr)' : layout === 'four_column' ? 'repeat(4, 1fr)' : 'repeat(3, 1fr)';

  // Format Date of Birth to display Day first, then Month, then Year (DD-MM-YYYY)
  const formatDob = (dobStr) => {
    if (!dobStr || dobStr === '—' || dobStr === 'N/A') return '—';
    try {
      const clean = String(dobStr).trim().split('T')[0];
      const ymd = clean.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/);
      if (ymd) {
        const year = ymd[1];
        const month = ymd[2].padStart(2, '0');
        const day = ymd[3].padStart(2, '0');
        return `${day}-${month}-${year}`;
      }
      const dmy = clean.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/);
      if (dmy) {
        const day = dmy[1].padStart(2, '0');
        const month = dmy[2].padStart(2, '0');
        const year = dmy[3];
        return `${day}-${month}-${year}`;
      }
      const d = new Date(clean);
      if (!isNaN(d.getTime())) {
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}-${month}-${year}`;
      }
    } catch (e) {}
    return dobStr;
  };

  return (
    <div style={{ background: isKindergarten ? '#faf5ff' : '#f8fafc', border: `1px solid ${isKindergarten ? '#e9d5ff' : '#e2e8f0'}`, borderRadius: '8px', padding: '12px 16px', marginBottom: '18px', display: 'flex', gap: '16px', alignItems: 'center' }}>
      <div style={{ flex: 1, display: 'grid', gridTemplateColumns: gridCols, gap: '10px', fontSize: '11px' }}>
        <div><strong style={{ color: '#64748b' }}>Student Name:</strong> <span style={{ fontWeight: '700', color: '#0f172a' }}>{student.name || '—'}</span></div>
        {cfg.showStudentId !== false && <div><strong style={{ color: '#64748b' }}>Student ID:</strong> <span style={{ fontWeight: '600' }}>{student.studentId || '—'}</span></div>}
        {cfg.showAdmissionNo !== false && <div><strong style={{ color: '#64748b' }}>Admission No:</strong> <span style={{ fontWeight: '600' }}>{student.admissionNumber || student.admissionNo || '—'}</span></div>}
        {cfg.showRollNo !== false && <div><strong style={{ color: '#64748b' }}>Roll No:</strong> <span style={{ fontWeight: '600' }}>{student.rollNo || student.rollNumber || '—'}</span></div>}
        {cfg.showClassSection !== false && <div><strong style={{ color: '#64748b' }}>Class & Sec:</strong> <span style={{ fontWeight: '600' }}>{student.className || '—'}{student.section ? ` - ${student.section}` : ''}</span></div>}
        {cfg.showDOB !== false && <div><strong style={{ color: '#64748b' }}>Date of Birth:</strong> <span style={{ fontWeight: '600' }}>{formatDob(student.dob || student.dateOfBirth)}</span></div>}
        {cfg.showParentName !== false && <div><strong style={{ color: '#64748b' }}>Parent/Guardian:</strong> <span style={{ fontWeight: '600' }}>{student.parentName || student.fatherName || '—'}</span></div>}
        <div><strong style={{ color: '#64748b' }}>Academic Session:</strong> <span style={{ fontWeight: '600' }}>{academicYear || '—'}</span></div>
        <div><strong style={{ color: '#64748b' }}>Evaluation Term:</strong> <span style={{ fontWeight: '600' }}>{term || '—'}</span></div>
        {cfg.showSatsNo !== false && student.satsNo && <div><strong style={{ color: '#64748b' }}>SATS No:</strong> <span style={{ fontWeight: '600' }}>{student.satsNo}</span></div>}
        {cfg.showPenNo !== false && student.penNo && <div><strong style={{ color: '#64748b' }}>PEN No:</strong> <span style={{ fontWeight: '600' }}>{student.penNo}</span></div>}
        {cfg.showGender !== false && student.gender && <div><strong style={{ color: '#64748b' }}>Gender:</strong> <span style={{ fontWeight: '600' }}>{student.gender}</span></div>}
        {cfg.showAadhaar !== false && student.aadhaarNumber && <div><strong style={{ color: '#64748b' }}>Aadhaar No:</strong> <span style={{ fontWeight: '600' }}>{student.aadhaarNumber}</span></div>}
        {cfg.showMotherName !== false && student.motherName && <div><strong style={{ color: '#64748b' }}>Mother's Name:</strong> <span style={{ fontWeight: '600' }}>{student.motherName}</span></div>}
        {cfg.showCaste !== false && student.caste && <div><strong style={{ color: '#64748b' }}>Caste:</strong> <span style={{ fontWeight: '600' }}>{student.caste}</span></div>}
        {cfg.showMotherTongue !== false && student.motherTongue && <div><strong style={{ color: '#64748b' }}>Mother Tongue:</strong> <span style={{ fontWeight: '600' }}>{student.motherTongue}</span></div>}
      </div>

      {photoPos === 'student_info' && (
        <img
          src={imgSrc}
          alt=""
          onError={() => setImgSrc(DEFAULT_AVATAR)}
          style={{ width: '65px', height: '80px', borderRadius: '6px', border: '2px solid #cbd5e1', objectFit: 'cover', backgroundColor: '#f1f5f9' }}
        />
      )}
    </div>
  );
};

export default StudentInformation;
